// Structure and Tailwind classNames ported from the Lovable reference
// implementation (files/snapdraw-design-kit/code/src/routes/signup.tsx),
// built on the shared AuthLayout. supabase.auth.signUp() carries the
// business name via options.data so handle_new_user() (0031) can set it on
// the account row it creates atomically — see the fix note in
// handleSubmit for why a separate follow-up update (the original approach)
// doesn't work whenever email confirmation is required (backlog AUTH-11).
// Field validation (required/invalid-email/required) ported from the kit's
// 2026-08-22 "form validation & error states" update: validates on submit,
// clears per-field as the user types, renders via the shared FieldError
// component instead of the old bare destructive-text paragraph. The success
// message (check-your-email confirmation) keeps its own green styling —
// only the error path changed.
// Errors store i18n KEYS (not resolved strings) and re-resolve via t() at
// render time, so they stay correct if the user flips the language toggle
// after an error appears (2026-08-22 fix). The real Supabase sign-up error
// is genuinely untranslatable third-party text (see Message's `raw` case in
// @/lib/i18n/message) — it can't follow the toggle because no Spanish
// version of it exists anywhere in our system.
// Password field has a show/hide toggle (2026-08-22) via the shared
// PasswordInput component.
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AtSign, Building2, CheckCircle2, Loader2 } from "lucide-react";

import { AuthLayout, authFieldClassName, authPrimaryClass } from "@/components/snapdraw/auth-layout";
import { FieldError } from "@/components/snapdraw/form";
import { PasswordInput } from "@/components/snapdraw/password-input";
import { AuthDivider, SsoButtons } from "@/components/snapdraw/sso-buttons";
import { clearOAuthRedirectMarker, markOAuthRedirectStarted } from "@/lib/auth-link-error";
import { supabase } from "@/lib/supabase";
import { useT } from "@/lib/i18n";
import { resolveMessage, type Message } from "@/lib/i18n/message";
import { cn } from "@/lib/utils";
import "@/styles/tailwind-preview.css";

interface FieldErrorKeys {
  email?: string;
  password?: string;
  business?: string;
}

// Same rule set as ResetPasswordPage's real password-strength check —
// signup previously only checked "non-empty," letting a 1-character
// password through client-side (backlog AUTH-1).
function isWeakPassword(value: string): boolean {
  return value.length < 8 || !/\d/.test(value) || !/[a-zA-Z]/.test(value);
}

export function SignUpPage() {
  const navigate = useNavigate();
  const t = useT();
  const [searchParams] = useSearchParams();
  // AUTH-5: `?invite=<email>` is purely a client-side UI-mode switch (skip
  // the business-name field, adjust copy) — it carries no security weight.
  // The actual account-join decision happens server-side in
  // accept_pending_invite(), keyed by the confirmed session's real email,
  // regardless of how the signup page was reached.
  const inviteEmail = searchParams.get("invite");
  const isInvite = Boolean(inviteEmail);
  const [email, setEmail] = useState(inviteEmail ?? "");
  const [password, setPassword] = useState("");
  const [business, setBusiness] = useState("");
  const [errors, setErrors] = useState<FieldErrorKeys>({});
  const [showSuccessConfirm, setShowSuccessConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<Message | null>(null);
  const [busy, setBusy] = useState(false);

  function clearError(key: keyof FieldErrorKeys) {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setShowSuccessConfirm(false);

    const nextErrors: FieldErrorKeys = {};
    if (!email.trim()) nextErrors.email = "auth.error.required";
    else if (!email.includes("@")) nextErrors.email = "auth.error.email";
    if (!password.trim()) nextErrors.password = "auth.error.required";
    else if (isWeakPassword(password)) nextErrors.password = "auth.error.weakPassword";
    if (!isInvite && !business.trim()) nextErrors.business = "auth.error.required";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setBusy(true);
    try {
      // Business name travels through raw_user_meta_data so the DB trigger
      // (handle_new_user(), supabase/migrations/0031) can set it directly on
      // the account row it creates — atomic, and independent of whether a
      // session exists yet. A prior version tried a follow-up authenticated
      // update instead, which silently discarded the business name whenever
      // email confirmation was required (no session = RLS-blocked, no
      // error) — backlog AUTH-11.
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: isInvite ? undefined : { data: { business_name: business.trim() } },
      });
      if (error) throw error;

      // Supabase doesn't throw for signing up with an email that already has
      // a confirmed account — as a deliberate anti-enumeration measure, it
      // returns a success-shaped response with an empty `identities` array
      // instead (backlog AUTH-1).
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        setSubmitError({ key: "auth.error.duplicateAccount" });
        return;
      }

      if (data.session) {
        navigate("/");
      } else {
        setShowSuccessConfirm(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (/already registered|already exists/i.test(message)) {
        setSubmitError({ key: "auth.error.duplicateAccount" });
      } else {
        setSubmitError(err instanceof Error ? { raw: err.message } : { key: "auth.error.generic" });
      }
    } finally {
      setBusy(false);
    }
  }

  // Google/Microsoft sign-up (backlog AUTH-8, 2026-09-04): creates the
  // account via the same handle_new_user() trigger as a password signup,
  // just with no business_name in the (nonexistent) signup metadata — the
  // account lands with business_name null, and App.tsx's access gate routes
  // straight to /onboarding/business until that's filled in. Supabase's own
  // provider slug for Microsoft is "azure" (Azure AD), not "microsoft".
  async function startOAuth(provider: "google" | "azure") {
    setSubmitError(null);
    // Busy immediately, not just for the password form's own submit
    // (backlog AUTH-16) — signInWithOAuth() doesn't navigate away
    // synchronously, so a double-click on either provider button
    // previously fired two calls before the redirect actually left the
    // page.
    setBusy(true);
    markOAuthRedirectStarted();
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (authError) {
      setSubmitError({ raw: authError.message });
      setBusy(false);
      clearOAuthRedirectMarker();
    }
  }

  return (
    <AuthLayout
      eyebrow={isInvite ? t("signup.inviteEyebrow") : t("signup.eyebrow")}
      footer={
        showSuccessConfirm ? undefined : (
          <p className="mt-6 text-center text-sm text-foreground/80">
            {t("signup.havePrompt")}
            <Link to="/login" className="ml-1.5 text-arc hover:text-spark">
              {t("signup.login")}
            </Link>
          </p>
        )
      }
    >
      {showSuccessConfirm ? (
        <div className="mt-6 flex flex-col items-center text-center">
          <CheckCircle2 aria-hidden className="h-16 w-16 text-arc" />
          <h2 className="mt-6 font-display text-2xl uppercase tracking-wide text-foreground">
            {t("signup.checkYourInbox")}
          </h2>
          <p className="mt-3 max-w-sm text-sm text-foreground/80">
            {t("signup.confirmationSentTo", { email })}
          </p>
          <a href={`mailto:${email}`} className={cn(authPrimaryClass, "mt-6 inline-flex items-center justify-center")}>
            {t("signup.openEmailApp")}
          </a>
          <Link to="/login" className="mt-4 text-sm text-arc hover:text-spark">
            {t("signup.backToLogin")}
          </Link>
        </div>
      ) : (
        <>
          {submitError && (
            <div className="mt-6">
              <FieldError>{resolveMessage(t, submitError)}</FieldError>
            </div>
          )}

          <SsoButtons
            disabled={busy}
            onGoogle={() => startOAuth("google")}
            onMicrosoft={() => startOAuth("azure")}
          />
          <AuthDivider />

          <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="space-y-2">
              <label className="relative flex items-center">
                <AtSign className="pointer-events-none absolute left-4 h-4 w-4 text-arc" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError("email");
                  }}
                  placeholder={t("login.email")}
                  autoComplete="email"
                  className={cn(authFieldClassName(!!errors.email), errors.email && "pr-10")}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "signup-email-error" : undefined}
                />
              </label>
              {errors.email && <FieldError id="signup-email-error">{t(errors.email)}</FieldError>}
            </div>

            <div className="space-y-2">
              <PasswordInput
                invalid={!!errors.password}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearError("password");
                }}
                placeholder={t("signup.passwordPlaceholder")}
                aria-label={t("signup.passwordPlaceholder")}
                autoComplete="new-password"
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? "signup-password-error" : undefined}
              />
              {errors.password && <FieldError id="signup-password-error">{t(errors.password)}</FieldError>}
            </div>

            {isInvite ? (
              <p className="pt-1 text-sm text-muted-foreground">{t("signup.inviteHelp")}</p>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="relative flex items-center">
                    <Building2 className="pointer-events-none absolute left-4 h-4 w-4 text-arc" />
                    <input
                      type="text"
                      value={business}
                      onChange={(e) => {
                        setBusiness(e.target.value);
                        clearError("business");
                      }}
                      placeholder={t("signup.businessPlaceholder")}
                      autoComplete="organization"
                      className={cn(authFieldClassName(!!errors.business), errors.business && "pr-10")}
                      aria-invalid={!!errors.business}
                      aria-describedby={errors.business ? "signup-business-error" : undefined}
                    />
                  </label>
                  {errors.business && <FieldError id="signup-business-error">{t(errors.business)}</FieldError>}
                </div>

                <p className="pt-1 text-sm text-muted-foreground">{t("signup.businessHelp")}</p>
              </>
            )}

            <button type="submit" disabled={busy} className={authPrimaryClass}>
              {busy ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
                  {t("signup.creatingAccount")}
                </span>
              ) : (
                t("signup.submitButton")
              )}
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
